import { create } from 'zustand';

import { getOrders } from '@/features/account/api/get-orders';
import { CancelledEvent, OrderStatus, OrderType, RejectedEvent } from '@/types';
// eslint-disable-next-line import/no-cycle
import {
  handleOrderForStore,
  orderResponseToOrder,
} from '@/utils/handle-store-data';

import { useAccountStore } from './use-account-store';

export type NewOrder = {
  id: string;
  symbol: string;
  isBuy: boolean;
  orderType: OrderType;
  limitPrice: bigint;
  size: bigint;
  reduceOnly: boolean;
  status: OrderStatus.NEW;
  initMarginRatio: bigint;
  postTime: number;
  tpPrice?: bigint;
  slPrice?: bigint;
};

export type TPSLOrder = {
  symbol: string;
  id: string;
  isBuy: boolean;
  orderType: OrderType.TAKE_PROFIT | OrderType.STOP_LOSS;
  limitPrice: string;
  size: string;
  reduceOnly?: boolean;
  initMarginRatio?: string;
};

export type FilledOrder = {
  id: string;
  symbol: string;
  isBuy: boolean;
  orderType: OrderType;
  limitPrice: bigint;
  tpPrice?: bigint;
  slPrice?: bigint;
  size: bigint;
  reduceOnly: boolean;
  status: OrderStatus.FILLED;
  initMarginRatio: bigint;
  lastFilledSize: bigint;
  lastFilledPrice: bigint;
  lastFilledTime: number;
  avgFilledPrice: bigint;
  realizedPnl: bigint;
  settledFunding: bigint;
  fees: bigint;
  postTime: number;
  markPrice: bigint;
};

export interface PartiallyFilledOrder extends Omit<FilledOrder, 'status'> {
  status: OrderStatus.PARTIALLY_FILLED;
  parentId: string;
}

export type CancelledOrder = {
  id: string;
  symbol: string;
  isBuy: boolean;
  orderType: OrderType;
  limitPrice: bigint;
  tpPrice?: bigint;
  slPrice?: bigint;
  size: bigint;
  reduceOnly: boolean;
  status: OrderStatus.CANCELLED;
  initMarginRatio: bigint;
  postTime: number;
};

export type RejectedOrder = {
  id: string;
  symbol: string;
  isBuy: boolean;
  orderType: OrderType;
  limitPrice: bigint;
  tpPrice?: bigint;
  slPrice?: bigint;
  size: bigint;
  reduceOnly: boolean;
  status: OrderStatus.REJECTED;
  initMarginRatio: bigint;
  postTime: number;
  code: number;
};

export type Order =
  | NewOrder
  | FilledOrder
  | CancelledOrder
  | RejectedOrder
  | PartiallyFilledOrder;

export type WalletScopedOrders = {
  orders: Record<string, Order>;
  filledOrderIds: Set<string>;
  newOrderIds: Set<string>;
  rejectedOrderIds: Set<string>;
  cancelledOrderIds: Set<string>;
  ordersToPoll: [string, OrderStatus, number][];
  pollOrdersIntervalId: number | null;
  maxFilledStartTime: number; // used for getMarks to determine whether it needs API or store
};

interface OrdersStore {
  refetchInterval: number;
  byWallet: Record<string, WalletScopedOrders>;

  creationFormOrderType?: OrderType;
  creationFormEstimateFillPrice?: number;
  creationFormTPPrice?: number;
  creationFormSLPrice?: number;

  getOrders: (wallet: string | undefined) => WalletScopedOrders;
  setOrder: (
    order: Order | CancelledEvent | RejectedEvent,
    address?: string,
  ) => void;
  pollOrders: (
    newOrdersToPoll: [string, OrderStatus, number][],
    delay?: number,
  ) => void;
  clearOrdersStore: () => void;
  setMaxFilledStartTime: (time: number) => void;
}

export const cloneDefaultWalletScopedOrders = (): WalletScopedOrders => ({
  orders: {},
  filledOrderIds: new Set(),
  newOrderIds: new Set(),
  rejectedOrderIds: new Set(),
  cancelledOrderIds: new Set(),
  ordersToPoll: [],
  pollOrdersIntervalId: null,
  maxFilledStartTime: useAccountStore.getState().getAccurateTime(),
});

export const useOrdersStore = create<OrdersStore>((set, get) => ({
  refetchInterval: 15_000,
  byWallet: {},

  creationFormOrderType: undefined,
  creationFormEstimateFillPrice: undefined,
  creationFormTPPrice: undefined,
  creationFormSLPrice: undefined,

  getOrders: (wallet) => {
    if (!wallet) return cloneDefaultWalletScopedOrders();

    const store = get().byWallet[wallet];
    return store ?? cloneDefaultWalletScopedOrders();
  },
  setMaxFilledStartTime: (time) => {
    const wallet = useAccountStore.getState().address;
    if (!wallet) {
      console.error(time, 'No wallet found in orders store');
      return;
    }
    const state = get();
    const store = state.byWallet[wallet] ?? cloneDefaultWalletScopedOrders();
    set({
      ...state,
      byWallet: {
        ...state.byWallet,
        [wallet]: { ...store, maxFilledStartTime: time },
      },
    });
  },
  setOrder: (order, addressOverride) => {
    const wallet = addressOverride ?? useAccountStore.getState().address;
    if (!wallet) {
      console.error(order, 'No wallet found in orders store');
      return;
    }

    const state = get();
    const store =
      wallet in state.byWallet
        ? { ...state.byWallet[wallet] }
        : cloneDefaultWalletScopedOrders();

    const existingOrder = store.orders[order.id];

    if (existingOrder) {
      const isTerminalStatus =
        existingOrder.status === OrderStatus.FILLED ||
        existingOrder.status === OrderStatus.PARTIALLY_FILLED ||
        existingOrder.status === OrderStatus.CANCELLED ||
        existingOrder.status === OrderStatus.REJECTED;
      const isOptimisticNew = order.status === OrderStatus.NEW;

      if (isTerminalStatus && isOptimisticNew) {
        return;
      }
    }

    const updatedOrder = existingOrder ? { ...existingOrder, ...order } : order;

    store.orders[order.id] = updatedOrder as Order;

    switch (order.status) {
      case OrderStatus.NEW:
        if (
          !store.cancelledOrderIds.has(order.id) &&
          !store.filledOrderIds.has(order.id)
        ) {
          store.newOrderIds.add(order.id);
        }
        break;
      case OrderStatus.FILLED:
      case OrderStatus.PARTIALLY_FILLED:
        store.filledOrderIds.add(order.id);
        store.newOrderIds.delete(order.id);
        if (order.lastFilledTime < store.maxFilledStartTime) {
          store.maxFilledStartTime = order.lastFilledTime;
        }
        break;
      case OrderStatus.REJECTED:
        store.rejectedOrderIds.add(order.id);
        store.newOrderIds.delete(order.id);
        break;
      case OrderStatus.CANCELLED:
        store.cancelledOrderIds.add(order.id);
        store.newOrderIds.delete(order.id);
        break;
    }

    set({
      byWallet: {
        ...state.byWallet,
        [wallet]: store,
      },
    });
  },

  pollOrders: (newOrdersToPoll, delay = 3_000) => {
    const { address } = useAccountStore.getState();
    if (!address) return;

    let pollOrdersIntervalId: number | null;

    set((state) => {
      pollOrdersIntervalId =
        state.byWallet[address]?.pollOrdersIntervalId ?? null;
      return {
        byWallet: {
          ...state.byWallet,
          [address]: {
            ...cloneDefaultWalletScopedOrders(),
            ...(state.byWallet[address] ?? {}),
            ordersToPoll: [
              ...(state.byWallet[address]?.ordersToPoll ?? []),
              ...newOrdersToPoll,
            ],
          },
        },
      };
    });

    const startPolling = () => {
      if (!pollOrdersIntervalId) {
        const intervalId = window.setInterval(() => {
          const now = useAccountStore.getState().getAccurateTime();
          const {
            ordersToPoll,
            filledOrderIds,
            newOrderIds,
            rejectedOrderIds,
            cancelledOrderIds,
          } = get().byWallet[address];

          const remaining = ordersToPoll.filter(([id, status, time]) => {
            if (now - time > 10_000) return false;
            if (status === OrderStatus.NEW)
              return (
                !newOrderIds.has(id) &&
                !filledOrderIds.has(id) &&
                !rejectedOrderIds.has(id) &&
                !cancelledOrderIds.has(id)
              );
            if (status === OrderStatus.FILLED)
              return (
                !filledOrderIds.has(id) &&
                !rejectedOrderIds.has(id) &&
                !cancelledOrderIds.has(id)
              );
            if (status === OrderStatus.REJECTED)
              return !rejectedOrderIds.has(id);
            if (status === OrderStatus.CANCELLED)
              return !cancelledOrderIds.has(id);
            return false;
          });

          set((state) => ({
            byWallet: {
              ...state.byWallet,
              [address]: {
                ...state.byWallet[address],
                ordersToPoll: remaining,
                pollOrdersIntervalId:
                  remaining.length === 0
                    ? null
                    : state.byWallet[address].pollOrdersIntervalId,
              },
            },
          }));

          // store.ordersToPoll = remaining;

          if (remaining.length === 0) {
            clearInterval(intervalId);
          } else {
            getOrders({ id: remaining.map(([id]) => id) }).then((res) =>
              res.forEach((data) => {
                const order = orderResponseToOrder(data);
                handleOrderForStore(order);
              }),
            );
          }
        }, 1500);

        set((state) => ({
          byWallet: {
            ...state.byWallet,
            [address]: {
              ...state.byWallet[address],
              pollOrdersIntervalId: intervalId,
            },
          },
        }));
      }
    };

    if (delay > 0) {
      setTimeout(startPolling, delay);
    } else {
      startPolling();
    }
  },

  clearOrdersStore: () => {
    const { address: wallet, getAccurateTime } = useAccountStore.getState();
    if (!wallet) {
      console.error('No wallet found in orders store clear function.');
      return;
    }

    const store = get().byWallet[wallet];
    if (store?.pollOrdersIntervalId) clearInterval(store.pollOrdersIntervalId);

    set((state) => ({
      byWallet: {
        ...state.byWallet,
        [wallet]: {
          orders: {},
          filledOrderIds: new Set(),
          newOrderIds: new Set(),
          rejectedOrderIds: new Set(),
          cancelledOrderIds: new Set(),
          ordersToPoll: [],
          pollOrdersIntervalId: null,
          maxFilledStartTime: getAccurateTime(),
        },
      },
    }));
  },
}));
